import React from 'react';
import { ChevronDown, ChevronRight, CheckCircle, MinusCircle } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { CapacityConfidence, ConsolidationSuggestion } from '../../../../../types/consolidation';

/** Stabil nøkkel for et forslag — vare + mål-bin er unik per analyse. */
export const suggestionKey = (s: ConsolidationSuggestion): string =>
    `${s.itemNo}::${s.target.binCode}`;

interface SuggestionTableProps {
    suggestions: ConsolidationSuggestion[];
    selectedKeys: Set<string>;
    expandedKeys: Set<string>;
    onToggleSelect: (key: string) => void;
    onToggleExpand: (key: string) => void;
    onSelectAll: (select: boolean) => void;
    disabled: boolean;
}

/**
 * Forslagstabell for konsolidering — én rad per vare/mål-bin med
 * ekspanderbare kildelinjer (chevron). Mønster fra HeatmapBatchMoveModal.
 */
export const SuggestionTable: React.FC<SuggestionTableProps> = ({
    suggestions,
    selectedKeys,
    expandedKeys,
    onToggleSelect,
    onToggleExpand,
    onSelectAll,
    disabled
}) => {
    const { t } = useTranslation();

    const confidenceLabel = (c: CapacityConfidence): string => {
        switch (c) {
            case 'observed': return t('manager.modules.consolidation.table.capObserved', {}, 'observert');
            case 'estimated': return t('manager.modules.consolidation.table.capEstimated', {}, 'ca.');
            default: return t('manager.modules.consolidation.table.capUnknown', {}, 'ukjent');
        }
    };

    const capacityText = (s: ConsolidationSuggestion): string => {
        if (s.target.capacityConfidence === 'unknown') {
            return t('manager.modules.consolidation.table.capacityUnknown', {}, 'kap. ukjent');
        }
        return t(
            'manager.modules.consolidation.table.capacity',
            { '0': s.target.maxQuantity, '1': confidenceLabel(s.target.capacityConfidence) },
            `kap. ${s.target.maxQuantity} · ${confidenceLabel(s.target.capacityConfidence)}`
        );
    };

    const allSelected = suggestions.length > 0 && suggestions.every(s => selectedKeys.has(suggestionKey(s)));

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                    <tr>
                        <th className="px-2 py-2 w-8"></th>
                        <th className="px-2 py-2 w-8">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                disabled={disabled}
                                onChange={() => onSelectAll(!allSelected)}
                                title={t('manager.modules.consolidation.table.selectAll', {}, 'Velg alle / fjern alle')}
                            />
                        </th>
                        <th className="px-3 py-2 text-left">{t('warehouse.general.item', {}, 'Vare')}</th>
                        <th className="px-3 py-2 text-left">{t('manager.modules.consolidation.table.sources', {}, 'Kilder')}</th>
                        <th className="px-3 py-2 text-left">{t('manager.modules.consolidation.table.target', {}, 'Mål')}</th>
                        <th className="px-3 py-2 text-right">{t('manager.modules.consolidation.table.frees', {}, 'Frigjør')}</th>
                        <th className="px-3 py-2 text-left">{t('manager.modules.consolidation.table.warnings', {}, 'Advarsler')}</th>
                    </tr>
                </thead>
                <tbody>
                    {suggestions.map(s => {
                        const key = suggestionKey(s);
                        const isSelected = selectedKeys.has(key);
                        const isExpanded = expandedKeys.has(key);
                        return (
                            <React.Fragment key={key}>
                                <tr className={`border-t border-gray-100 hover:bg-gray-50 ${isSelected ? '' : 'opacity-60'}`}>
                                    <td className="px-2 py-2">
                                        <button
                                            type="button"
                                            onClick={() => onToggleExpand(key)}
                                            className="p-0.5 rounded hover:bg-gray-200 text-gray-500"
                                            aria-label={t('manager.modules.consolidation.table.expand', {}, 'Vis kildelinjer')}
                                        >
                                            {isExpanded
                                                ? <ChevronDown className="w-4 h-4" />
                                                : <ChevronRight className="w-4 h-4" />}
                                        </button>
                                    </td>
                                    <td className="px-2 py-2">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={disabled}
                                            onChange={() => onToggleSelect(key)}
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-mono text-xs font-semibold text-gray-800">{s.itemNo}</div>
                                        {s.description1 && (
                                            <div className="text-xs text-gray-500 truncate max-w-xs">{s.description1}</div>
                                        )}
                                        <div className="text-[10px] text-gray-400">
                                            {t('manager.modules.consolidation.table.binType', { '0': s.binTypeNo }, `Bintype ${s.binTypeNo}`)}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-700">
                                        {t(
                                            'manager.modules.consolidation.table.sourcesSummary',
                                            { '0': s.sources.length, '1': s.totalQuantity },
                                            `${s.sources.length} bins · ${s.totalQuantity} stk`
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-mono text-xs font-semibold text-gray-800">{s.target.binCode}</div>
                                        <div className="text-xs text-gray-500">{capacityText(s)}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                                            +{s.binsFreed}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {s.warnings.length === 0 ? (
                                            <span className="text-gray-300">—</span>
                                        ) : (
                                            <div className="flex flex-wrap items-center gap-1">
                                                {s.warnings.slice(0, 2).map((w, i) => (
                                                    <span
                                                        key={i}
                                                        title={w}
                                                        className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800 max-w-[180px] truncate inline-block"
                                                    >
                                                        {w}
                                                    </span>
                                                ))}
                                                {s.warnings.length > 2 && (
                                                    <span
                                                        className="text-[10px] text-yellow-800 font-medium"
                                                        title={s.warnings.slice(2).join('\n')}
                                                    >
                                                        {t(
                                                            'manager.modules.consolidation.table.moreWarnings',
                                                            { '0': s.warnings.length - 2 },
                                                            `+${s.warnings.length - 2} flere`
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr className="border-t border-gray-100 bg-gray-50">
                                        <td colSpan={7} className="px-4 py-3">
                                            <div className="text-xs text-gray-600 mb-2">
                                                {t(
                                                    'manager.modules.consolidation.table.fillAfter',
                                                    { '0': s.target.fillPctAfter },
                                                    `Fyllgrad i mål etter flytt: ${s.target.fillPctAfter} %`
                                                )}
                                            </div>
                                            <table className="w-full text-xs bg-white border border-gray-200 rounded">
                                                <thead className="bg-gray-50 text-gray-600">
                                                    <tr>
                                                        <th className="px-2 py-1.5 text-left">{t('manager.modules.consolidation.table.fromBin', {}, 'Fra lokasjon')}</th>
                                                        <th className="px-2 py-1.5 text-right">{t('manager.modules.consolidation.table.quantity', {}, 'Antall')}</th>
                                                        <th className="px-2 py-1.5 text-left">{t('manager.modules.consolidation.table.effect', {}, 'Effekt')}</th>
                                                        <th className="px-2 py-1.5 text-left">{t('manager.modules.consolidation.table.warnings', {}, 'Advarsler')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {s.sources.map((src, i) => (
                                                        <tr key={`${src.fromBinCode}-${i}`} className="border-t border-gray-100">
                                                            <td className="px-2 py-1.5 font-mono">{src.fromBinCode}</td>
                                                            <td className="px-2 py-1.5 text-right">{src.quantity}</td>
                                                            <td className="px-2 py-1.5">
                                                                {src.freesBin ? (
                                                                    <span className="inline-flex items-center text-green-700">
                                                                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                                        {t('manager.modules.consolidation.table.freesBin', {}, 'Frigjøres')}
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center text-gray-500">
                                                                        <MinusCircle className="w-3.5 h-3.5 mr-1" />
                                                                        {t('manager.modules.consolidation.table.residueLeft', {}, 'Restsaldo igjen')}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-1.5">
                                                                {src.warnings.length === 0 ? (
                                                                    <span className="text-gray-300">—</span>
                                                                ) : (
                                                                    <span className="text-yellow-800">{src.warnings.join(' · ')}</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
